import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserRole } from '../enums/role-name';
import { UserProfileImage } from '../models/image';
import { EnterpriseStore } from '../stores/enterprise.store';
import { UserRoleStore } from '../stores/user-role.store';
import { ConsultingTeamMemberRole } from '../types/consulting-team-member';
import { Enterprise } from '../types/enterprise';
import { Facility } from '../types/facility';
import { Profile } from '../types/profile';
import { AuthService } from './../auth/auth.service';
import { AccountService } from './account.service';
import { ConsultingTeamMemberService } from './consulting-team-member.service';
import { CurrentUserStateService } from './current-user-state.service';
import { EnterpriseService } from './enterprise.service';
import { ExchangeRateService } from './exchange-rate.service';
import { FacilityService } from './facility.service';
import { MediaService } from './media.service';

export type Role = UserRole | ConsultingTeamMemberRole;

@Injectable({
  providedIn: 'root',
})
export class UserRoleService {
  private enterprises: Enterprise[] = [];
  enterpriseAdmin: { [enterpriseId: string]: boolean } = {};
  facilityAdmin: { [enterpriseId: string]: boolean } = {};
  facilityRoles: { [facilityId: string]: string } = {};
  enterpriseRoles: { [enterpriseId: string]: string } = {};
  consultingOrganizationRoles: { [consultingOrganizationId: string]: ConsultingTeamMemberRole[] } = {};

  accountOwnerEnterpriseId: string | undefined;
  hasEnterpriseAdmin = false;
  hasWorksiteAdmin = false;

  isLoaded = false;

  private profileSubj$: BehaviorSubject<Profile> = new BehaviorSubject<Profile>(null!);
  profile$ = this.profileSubj$.asObservable();
  profile?: Profile;

  private enterprisesSubj$ = new BehaviorSubject<Enterprise[]>([]);
  enterprises$ = this.enterprisesSubj$.asObservable();

  // Distinguishes "still loading" from "loaded with zero enterprises" for consumers
  // that render loading/empty states (e.g. the client switcher).
  private enterprisesLoadedSubj$ = new BehaviorSubject<boolean>(false);
  enterprisesLoaded$ = this.enterprisesLoadedSubj$.asObservable();

  // Set when the initial load fails, so consumers can render an error + retry state
  // instead of an eternal spinner.
  private enterprisesLoadFailedSubj$ = new BehaviorSubject<boolean>(false);
  enterprisesLoadFailed$ = this.enterprisesLoadFailedSubj$.asObservable();

  private consultingOrganizationIdSubj$ = new BehaviorSubject<string | null>(null);
  consultingOrganizationId$ = this.consultingOrganizationIdSubj$.asObservable();

  userProfileImage?: UserProfileImage;
  userProfileImage$: BehaviorSubject<UserProfileImage> = new BehaviorSubject<UserProfileImage>(null!);

  private currentUserIdSubj$: BehaviorSubject<string> = new BehaviorSubject<string>(null!);
  currentUserId$ = this.currentUserIdSubj$.asObservable();

  private enterpriseAdminsSubj$: BehaviorSubject<{ [enterpriseId: string]: boolean }> = new BehaviorSubject<{
    [enterpriseId: string]: boolean;
  }>(null!);
  enterpriseAdmins$ = this.enterpriseAdminsSubj$.asObservable();

  private settingsAccessSubj$ = new BehaviorSubject<boolean>(false);
  settingsAccess$ = this.settingsAccessSubj$.asObservable();

  constructor(
    private enterpriseService: EnterpriseService,
    private accountService: AccountService,
    private authService: AuthService,
    private facilityService: FacilityService,
    private consultingTeamMemberService: ConsultingTeamMemberService,
    private exchangeRateService: ExchangeRateService,
    private mediaService: MediaService,
    private currentUserState: CurrentUserStateService,
    private userRoleStore: UserRoleStore,
    private enterpriseStore: EnterpriseStore,
  ) {
    // Failures are reflected in enterprisesLoadFailed$ (and retried via retryLoad);
    // rethrowing here would only surface as an unhandled rejection.
    this.loadAll().catch(() => undefined);
  }

  clear(): void {
    this.enterprises = [];
    this.enterpriseAdmin = {};
    this.facilityAdmin = {};
    this.facilityRoles = {};
    this.enterpriseRoles = {};
    this.consultingOrganizationRoles = {};
    this.accountOwnerEnterpriseId = undefined;
    this.hasWorksiteAdmin = false;
    this.hasEnterpriseAdmin = false;
    this.settingsAccessSubj$.next(false);
    this.enterprisesSubj$.next([]);
    this.enterprisesLoadedSubj$.next(false);
    this.enterprisesLoadFailedSubj$.next(false);
    this.consultingOrganizationIdSubj$.next(null);

    this.isLoaded = false;
  }

  async loadAll(useCache = true) {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (this.isLoaded === true) {
      return;
    }

    this.isLoaded = true;
    this.enterprisesLoadFailedSubj$.next(false);

    try {
      const [_, enterprises, profile] = await Promise.all([
        this.exchangeRateService.loadCurrencyRates()?.toPromise(),
        this.enterpriseService.loadAll(),
        this.accountService.getProfile(useCache).toPromise(),
      ]);

      if (enterprises.length > 0) {
        const currentEnterpriseId = this.enterpriseStore.getCurrentEnterpriseId();
        const enterprise = enterprises.find((ent) => ent.id === currentEnterpriseId) || enterprises[0];
        const facilities = await this.facilityService.loadAll(enterprise.id);
        enterprise.facilities = (facilities || []).sort((a, b) => a.name.localeCompare(b.name));
      }

      this.enterprises = enterprises;
      this.enterprisesSubj$.next(enterprises);
      this.setProfile(profile);

      this.mediaService.getUserProfileImageByUserId(profile.uuid!).subscribe((image) => {
        this.userProfileImage = image;
        this.userProfileImage$.next(image);
      });

      await this.loadUserRole();

      this.enterprisesLoadedSubj$.next(true);
    } catch (error) {
      console.error('Error loading data', error);
      // Un-latch so an in-app retry (or the next login) can attempt a fresh load.
      this.isLoaded = false;
      this.enterprisesLoadFailedSubj$.next(true);
      throw error;
    }
  }

  /** In-app retry for a failed initial load (e.g. the client switcher's Retry button). */
  retryLoad(): Promise<void> {
    return this.loadAll().catch(() => undefined);
  }

  /**
   * Re-fetches the enterprise list and each enterprise's facilities so the client
   * switcher reflects clients added or removed since login. No-op while the initial
   * load is running - that load already fetches fresh data.
   */
  async refreshEnterprises(): Promise<void> {
    if (!this.authService.isAuthenticated() || !this.isLoaded) {
      return;
    }

    const enterprises = await this.enterpriseService.loadAll();
    this.enterpriseStore.enterprises = enterprises;
    this.enterprises = enterprises;
    this.enterprisesSubj$.next(enterprises);
  }

  async loadUserRole() {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (!this.currentUserState.userId) {
      return;
    }

    const assignments = await this.consultingTeamMemberService
      .getAssignmentsByTeamMemberId(this.currentUserState.userId)
      .toPromise();

    this.consultingOrganizationRoles = {};

    if (assignments && assignments.length > 0) {
      // Any assignment (whatever its scope) means the user belongs to that consulting org.
      // Executive (org-wide) assignments outrank scoped ones, and ties break on org id,
      // so members of several orgs get stable branding instead of whichever row the API
      // happened to return first.
      const ranked = [...assignments].sort((a, b) => {
        const aScoped = a.facilityId || a.enterpriseId ? 1 : 0;
        const bScoped = b.facilityId || b.enterpriseId ? 1 : 0;
        return aScoped - bScoped || a.consultingOrganizationId.localeCompare(b.consultingOrganizationId);
      });
      this.consultingOrganizationIdSubj$.next(ranked[0].consultingOrganizationId);

      assignments.forEach((assignment) => {
        const organizationRoles = this.consultingOrganizationRoles[assignment.consultingOrganizationId] ?? [];
        if (!organizationRoles.includes(assignment.role)) {
          organizationRoles.push(assignment.role);
        }

        this.consultingOrganizationRoles[assignment.consultingOrganizationId] = organizationRoles;

        if (assignment.facilityId) {
          this.facilityRoles[assignment.facilityId] = assignment.role;
        } else if (assignment.enterpriseId) {
          this.enterpriseRoles[assignment.enterpriseId] = assignment.role;
        } else {
          // If there's no facilityId or enterpriseId, it's an executive role
          this.userRoleStore.setCurrentUserRole(assignment.role);
        }
      });

      this.userRoleStore.setFacilityRoles(this.facilityRoles);
      this.userRoleStore.setEnterpriseRoles(this.enterpriseRoles);
    }
  }

  /**
   * Consulting membership is assignment-based (any consulting role, any scope) and
   * independent of the navigation-dependent "current" role the store adopts per
   * facility/enterprise selection. Snapshot of consultingOrganizationId$ - valid once
   * loadAll (which awaits loadUserRole) has completed.
   */
  isConsultingOrgMember(): boolean {
    return this.consultingOrganizationIdSubj$.value !== null;
  }

  isConsultingOrgExecutive(consultingOrganizationId: string | null): boolean {
    if (!consultingOrganizationId) {
      return false;
    }

    return (
      this.consultingOrganizationRoles[consultingOrganizationId]?.includes(ConsultingTeamMemberRole.EXECUTIVE) ?? false
    );
  }

  private setProfile(profile: Profile): void {
    this.profile = profile;
    this.profileSubj$.next(profile);
    this.currentUserIdSubj$.next(profile.uuid!);
    this.currentUserState.setUserId(profile.uuid!);

    profile.employments?.forEach((employment) => {
      employment.facilityEmployments?.forEach((facilityEmployment) => {
        if (facilityEmployment.facilityId) {
          this.facilityRoles[facilityEmployment.facilityId] = facilityEmployment.role?.name!;

          if (!this.enterpriseRoles[employment.enterpriseId!]) {
            this.enterpriseRoles[employment.enterpriseId!] = facilityEmployment.role?.name!;
          }
        }

        switch (facilityEmployment.role?.name as UserRole) {
          case UserRole.ACCOUNT_OWNER:
            this.accountOwnerEnterpriseId = employment.enterpriseId;
            this.hasEnterpriseAdmin = true;
            this.hasWorksiteAdmin = true;
            this.enterpriseAdmin[employment.enterpriseId!] = true;
            this.facilityAdmin[facilityEmployment.facilityId] = true;
            break;
          case UserRole.ENTERPRISE_ADMIN:
            this.hasEnterpriseAdmin = true;
            this.hasWorksiteAdmin = true;
            this.enterpriseAdmin[employment.enterpriseId!] = true;
            break;
          case UserRole.WORKSITE_ADMIN:
            this.hasWorksiteAdmin = true;
            this.facilityAdmin[facilityEmployment.facilityId] = true;
            break;
        }
      });
    });

    this.userRoleStore.setFacilityRoles(this.facilityRoles);
    this.userRoleStore.setEnterpriseRoles(this.enterpriseRoles);
    this.enterpriseAdminsSubj$.next(this.enterpriseAdmin);
    this.updateSettingsAccess();
  }

  getEnterprises(): Enterprise[] {
    return this.enterprises;
  }

  getFacilities(enterpriseId: string): Facility[] {
    return this.enterpriseStore.getEnterpriseFacilities(enterpriseId);
  }

  isEnterpriseAdmin(enterprise: Enterprise): boolean {
    return this.enterpriseAdmin[enterprise.id];
  }

  isWorksiteAdmin(facility: Facility): boolean {
    if (!facility.enterprise) {
      return false;
    }

    return this.facilityAdmin[facility.id] || this.isEnterpriseAdmin(facility.enterprise);
  }

  getRoleName(fac: Facility): string {
    return this.facilityRoles[fac.id]?.replace('_', ' ').toLowerCase();
  }

  getFirstRoleName(enterprise: Enterprise): string {
    return this.enterpriseRoles[enterprise.id]?.replace('_', ' ').toLowerCase();
  }

  getAdminEnterprises(): Enterprise[] {
    return this.enterprises.filter((e) => this.isEnterpriseAdmin(e));
  }

  getWorksiteAdminFacilities(): Facility[] {
    return this.getAllFacilities().filter((f) => this.isWorksiteAdmin(f));
  }

  getWorksiteAdminFacilitiesForEnterprise(enterprise: Enterprise): Facility[] {
    return this.getAllFacilities().filter((f) => this.isWorksiteAdmin(f) && f.enterprise?.id === enterprise.id);
  }

  private getAllFacilities(): Facility[] {
    return this.enterpriseStore.enterprises.reduce<Facility[]>((all, e) => all.concat(e.facilities ?? []), []);
  }

  isAccountOwner(): boolean {
    return !!this.accountOwnerEnterpriseId;
  }

  private updateSettingsAccess(): void {
    this.settingsAccessSubj$.next(this.hasWorksiteAdmin || this.hasEnterpriseAdmin || this.isAccountOwner());
  }

  currentUserUUID(): string | undefined {
    return this.profile?.uuid;
  }

  getCurrentUserProfileImage(): UserProfileImage | undefined {
    return this.userProfileImage;
  }

  reloadUserProfileImage() {
    this.mediaService.getUserProfileImageByUserId(this.profile?.uuid!).subscribe((image) => {
      this.userProfileImage = image;
      this.userProfileImage$.next(image);
    });
  }

  getFacilityEmployment(facility: Facility) {
    let result = undefined;
    if (this.profile && facility) {
      const employment = this.profile.employments?.find((e) => e.enterpriseId === facility.enterprise?.id);
      if (employment) {
        result = employment.facilityEmployments.find(
          (facilityEmployment) => facilityEmployment.facilityId === facility.id,
        );
      }
    }

    return result;
  }
}
